import React, { useState, useEffect } from 'react';
import { Button, Space, Tag } from 'antd';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, FormElement, SelectControl } from '../../utils/form';

const FILTER_TYPES = {
    SINGLE: 'single',
    MULTIPLE: 'multiple',
    DATE: 'date',
    GROUP: 'group',
};

const Filters = ({
    filterOptions = [],
    onFilterChange,
    handleClose,
    show
}) => {

    const location = useLocation();
    const navigate = useNavigate();

    // 🔥 FIX: initialize params BEFORE first render
   const getParamsFromURL = () => {
  const searchParams = new URLSearchParams(location.search);
  const params = {};

  filterOptions.forEach(filter => {
    const raw = searchParams.get(filter.field);
    if (!raw) return;

    switch (filter.type) {
      case FILTER_TYPES.MULTIPLE:
      case FILTER_TYPES.GROUP:
      case FILTER_TYPES.DATE:
        params[filter.field] = raw.split(',');
        break;

      default:
        params[filter.field] = raw;
    }
  });

  // preserve non-filter params like page
  searchParams.forEach((value, key) => {
    if (!(key in params)) {
      params[key] = value;
    }
  });

  return params;
};


    const [localParams, setLocalParams] = useState(() => getParamsFromURL());

    // Sync when URL changes (back/forward navigation)
    useEffect(() => {
        setLocalParams(getParamsFromURL());
    }, [location.search]);

    // valueLink system (kept untouched except initialization fix)
    const valueLink = (field) => {
        const filter = filterOptions.find(f => f.field === field);
        const isMulti = filter?.type === FILTER_TYPES.MULTIPLE;

        return {
            value: localParams[field] || (isMulti ? [] : ''),
            requestChange: (value) => {
                setLocalParams(prev => {
                    const updated = { ...prev };

                    if (value && (!Array.isArray(value) || value.length > 0)) {
                        updated[field] = value;
                    } else {
                        delete updated[field];
                    }
                    return updated;
                });
            }
        };
    };



    // Update URL
    const updateURLParams = (newParams) => {
        const searchParams = new URLSearchParams();

        Object.keys(newParams).forEach(key => {
            if (newParams[key]) {
                if (Array.isArray(newParams[key])) {
                    searchParams.set(key, newParams[key].join(','));
                } else {
                    searchParams.set(key, newParams[key]);
                }
            }
        });

        navigate({ search: searchParams.toString() }, { replace: true });

        if (onFilterChange) onFilterChange(newParams);
    };

    // Apply filters → reset to page 1
    const handleApplyFilters = () => {
        const newParams = { ...localParams, page: 1 };
        updateURLParams(newParams);
        handleClose?.();
    };

    const handleCancelFilters = () => {
        handleClose?.();
    };

    const handleResetFilters = () => {
        // remove params from the urls only related to filters
        const newParams = getParamsFromURL();
        filterOptions.forEach(filter => {
            delete newParams[filter.field];
        });
        newParams.page = 1;
        setLocalParams(newParams);
        updateURLParams(newParams);
        handleClose?.();
    };

    // Clear all
    const handleReset = () => {
        const newParams = { page: 1 };
        setLocalParams(newParams);
        updateURLParams(newParams);
    };

    const handleRemoveFilter = (field) => {
        const current = getParamsFromURL();
        delete current[field];
        current.page = 1;
        updateURLParams(current);
    };

    // Render all filters
    const renderCustomFilters = () =>
        filterOptions.map(filter => {
            switch (filter.type) {
                case FILTER_TYPES.SINGLE:
                    return (
                        <Col key={filter.field} md={12} className="mb-3 p-0">
                            <FormElement
                                valueLink={valueLink(filter.field)}
                                label={filter.label}
                                control={{
                                    type: SelectControl,
                                    settings: {
                                        options: filter.options || [],
                                        placeholder: `Select ${filter.label}`,
                                        multiple: false,
                                        searchable: true,
                                        canClear: true,
                                    },
                                }}
                            />
                        </Col>
                    );

                case FILTER_TYPES.MULTIPLE:
                    return (
                        <Col key={filter.field} md={12} className="mb-3 p-0">
                            <FormElement
                                valueLink={valueLink(filter.field)}
                                label={filter.label}
                                control={{
                                    type: SelectControl,
                                    settings: {
                                        options: filter.options || [],
                                        placeholder: `Select ${filter.label}`,
                                        multiple: true,
                                        searchable: true,
                                        selectAll: true,
                                    },
                                }}
                            />
                        </Col>
                    );

                case FILTER_TYPES.GROUP:
                    return (
                        <Col md={12} key={filter.field} className="mb-3 p-0">
                            <label className="form-label">{filter.label}</label>

                            {/* Parent multi-select */}
                            <FormElement
                                valueLink={valueLink(filter.field)}
                                control={{
                                    type: SelectControl,
                                    settings: {
                                        options: filter.groups.map(g => ({
                                            value: g.parent,
                                            label: g.parent
                                        })),
                                        placeholder: `Select ${filter.label}`,
                                        multiple: true,
                                        searchable: true,
                                    }
                                }}
                            />
                            <Row>
                                {/* Render child selects for selected parents */}
                                <Col md={12}>
                                {Array.isArray(localParams[filter.field]) &&
                                    localParams[filter.field].map(parent => {
                                        const group = filter.groups.find(g => g.parent === parent);
                                        if (!group) return null;

                                        return (
                                            <Col key={group.field} md={12} className="mt-3 p-0">
                                                <label className="form-label">{group.parent}</label>

                                                <FormElement
                                                    valueLink={valueLink(group.field)}
                                                    control={{
                                                        type: SelectControl,
                                                        settings: {
                                                            options: group.options,
                                                            placeholder: `Select ${group.parent}`,
                                                            multiple: true,
                                                            searchable: true,
                                                            selectAll: true,

                                                        }
                                                    }}
                                                />
                                            </Col>
                                        );
                                    })}
                                </Col>
                            </Row>
                        </Col>
                    );



                case FILTER_TYPES.DATE:
                    return (
                        <Col key={filter.field} md={12} className="mb-3 p-0">
                            <div className="form-group">
                                <label className="form-label">{filter.label}</label>
                                <div className="d-flex gap-2 align-items-center">
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={localParams[filter.field]?.[0] || ''}
                                        onChange={(e) => {
                                            const from = e.target.value;
                                            const to = localParams[filter.field]?.[1] || '';
                                            valueLink(filter.field).requestChange(
                                                from && to ? [from, to] : undefined
                                            );
                                        }}
                                    />
                                    <span className="text-muted">to</span>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={localParams[filter.field]?.[1] || ''}
                                        onChange={(e) => {
                                            const to = e.target.value;
                                            const from = localParams[filter.field]?.[0] || '';
                                            valueLink(filter.field).requestChange(
                                                from && to ? [from, to] : undefined
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        </Col>
                    );

                default:
                    return null;
            }
        });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (show && !event.target.closest('.right-tab')) {
                handleClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [show]);

    if (!show) return null;



    return (
        <div className="right-filter-bar">
            {filterOptions.length > 0 && (
                <div className="right-tab">
                    <div className="filter-header d-flex justify-content-between align-items-center">
                        <h4 className="f-s-20 inter-regular">Extended Filter</h4>
                        <Button type="text" onClick={handleCancelFilters}>
                            {crossIcon}
                        </Button>
                    </div>

                    <hr />

                    <Form>
                        <div className="pt-4">
                            {renderCustomFilters()}
                        </div>
                    </Form>

                    <div className="filter-actions d-flex mt-4 gap-8">
                        <button className="secondary-btn" onClick={handleResetFilters}>Reset</button>
                        <button className="primary-btn" onClick={handleApplyFilters}>Apply</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Filters;

const crossIcon = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 5L5 15M5 5L15 15" stroke="#1E1E1E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
